import { dpow } from "./dmath";
import { BALANCE } from "./balance";
import { eraCostMult } from "./perks";
import { addMod } from "./mods";
import { arcs, C, events, legends, themes, type Effect, type EventDef } from "@bd/content";
import { priceIndex, WEEKS_PER_YEAR, yearOf } from "./calendar";
import { addDecision, DECISION_HANDLERS } from "./company";
import { addWave, rivalActive } from "./market";
import { creditLimit, makeLoan } from "./production";
import type { Rng } from "./rng";
import type { ArcRun, Candidate, Company, GameState, PendingDecision, Product } from "./state";
import { makeStaff } from "./studio";
import { unlock } from "./unlocks";
import { clamp, digits, log, money, newId, rng } from "./util";
import { checkAchievements } from "./achievements";
import { linesWeek, offerLicence } from "./lines";

/**
 * The Surprise Director. It reads each company's tension and paces events and story arcs:
 * bored and rich gets a challenge, broke gets an opportunity (never a second disaster), a winning streak gets a rival move.
 * Hard rules: no event can end a company, every event offers a choice, nothing big repeats within 10 in-game years.
 */

const DEFAULT_COOLDOWN_YEARS = 25;
const OPPORTUNITY_KINDS = new Set(["opportunity", "weird", "culture"]);

export type Tension = Company["director"]["tension"];

export function tension(state: GameState, co: Company): Tension {
  const idx = priceIndex(state.week);
  const burn = Math.max(1, co.ledger.slice(-8).reduce((s, w) => s + w.costs, 0) / Math.max(1, Math.min(8, co.ledger.length)));
  const runway = co.cash / burn;
  const recent = co.products.filter((p) => p.launched && (p.kind === "base" || p.kind === "spinoff" || p.kind === "license")).sort((a, b) => b.releaseWeek - a.releaseWeek).slice(0, 3);
  if (co.cash < 1500 * idx || runway < 6 || (recent.length >= 2 && recent.slice(0, 2).every((p) => p.score < 5))) return "struggling";
  if (recent.length >= 2 && recent.slice(0, 2).every((p) => p.score >= 8)) return "winning";
  const lastRelease = recent[0]?.releaseWeek ?? 0;
  if (runway > 40 && state.week - Math.max(lastRelease, co.director.lastEventWeek) > 26) return "bored";
  return "calm";
}

// ------------------------------------------------------------------ targets ("needs")

const THEME_NEEDS: Record<string, string> = { petGames: "pets_vets", vikingGames: "vikings", gardenGames: "gardens", cyberGames: "cyberpunk", dungeonGames: "dungeon_crawl" };

function launched(co: Company): Product[] {
  return co.products.filter((p) => p.launched && !p.retired);
}

function latest(co: Company): Product | undefined {
  return launched(co).sort((a, b) => b.releaseWeek - a.releaseWeek)[0];
}

/** Picks the concrete things an event is about (which product, which person), or null if a need can't be met. */
function resolveNeeds(state: GameState, co: Company, needs: string[], r: Rng): Record<string, any> | null {
  const data: Record<string, any> = {};
  const hired = co.staff.filter((s) => !s.founder);
  for (const n of needs) {
    switch (n) {
      case "product": {
        const p = latest(co);
        if (!p) return null;
        data.product ??= p.id;
        break;
      }
      case "hit": {
        const hits = launched(co).filter((p) => p.score >= 8);
        if (!hits.length) return null;
        data.product = r.pick(hits).id;
        break;
      }
      case "oldProduct": {
        const old = launched(co).filter((p) => state.week - p.releaseWeek >= 10 * WEEKS_PER_YEAR);
        if (!old.length) return null;
        data.product = old.sort((a, b) => b.score - a.score)[0].id;
        break;
      }
      case "kidsProduct":
      case "ccgProduct":
      case "rpgProduct": {
        const want = n === "kidsProduct" ? (p: Product) => p.design.audience === "kids" : n === "ccgProduct" ? (p: Product) => p.design.format === "card_game" || p.design.format === "ccg" : (p: Product) => p.design.format === "rpg_book" || p.design.audience === "roleplayers";
        const list = launched(co).filter(want);
        if (!list.length) return null;
        data.product = list.sort((a, b) => b.releaseWeek - a.releaseWeek)[0].id;
        break;
      }
      case "stock": {
        const list = launched(co).filter((p) => p.stock > 50);
        if (!list.length) return null;
        data.product ??= list[0].id;
        break;
      }
      case "order":
        if (!co.products.some((p) => p.orders.length > 0)) return null;
        break;
      case "office":
        if (co.level === "garage") return null;
        break;
      case "studio":
        if (co.level === "garage" || co.level === "office") return null;
        break;
      case "staff":
        if (!hired.length) return null;
        data.staff = r.pick(hired).id;
        break;
      case "twoStaff":
        if (hired.length < 2) return null;
        data.staff = hired[0].id;
        data.staff2 = hired[1].id;
        break;
      case "tiredStaff": {
        const tired = co.staff.filter((s) => s.burnout > 40);
        if (!tired.length) return null;
        data.staff = tired[0].id;
        break;
      }
      case "crunching":
        if (!co.staff.some((s) => s.crunch)) return null;
        break;
      case "project": {
        const p = co.projects.find((x) => x.phase !== "ready");
        if (!p) return null;
        data.project = p.id;
        break;
      }
      case "pet":
        if (!co.pets.length) return null;
        data.pet = co.pets[0].id;
        break;
      case "bustRival": {
        const bust = state.rivals.find((x) => x.bust && x.catalogueFor);
        if (!bust) return null;
        data.rival = bust.id;
        break;
      }
      default: {
        const theme = THEME_NEEDS[n];
        if (theme) {
          const list = launched(co).filter((p) => p.design.theme === theme);
          if (!list.length) return null;
          data.product ??= list[0].id;
          break;
        }
        return null; // unknown need: never fire
      }
    }
  }
  return data;
}

function eligible(state: GameState, co: Company, e: EventDef, t: Tension): boolean {
  const year = yearOf(state.week);
  const w = e.when;
  if (w.minYear && year < w.minYear) return false;
  if (w.maxYear && year > w.maxYear) return false;
  if (w.tension && w.tension !== t) return false;
  if (w.maxPets !== undefined && co.pets.length > w.maxPets) return false;
  if (w.season && (state.week % WEEKS_PER_YEAR) < 46) return false;
  const last = co.director.fired[e.id];
  if (last !== undefined) {
    if (e.once) return false;
    if (state.week - last < (e.cooldownYears ?? DEFAULT_COOLDOWN_YEARS) * WEEKS_PER_YEAR) return false;
  }
  // broke and struggling: never a second disaster
  if (t === "struggling" && e.kind === "crisis") return false;
  return true;
}

function kindWeight(kind: EventDef["kind"], t: Tension): number {
  if (t === "struggling") return OPPORTUNITY_KINDS.has(kind) ? (kind === "opportunity" ? 3 : 1.2) : 0.4;
  if (t === "bored") return kind === "crisis" || kind === "industry" ? 2 : kind === "people" ? 1.5 : 1;
  if (t === "winning") return kind === "industry" || kind === "crisis" ? 1.8 : 1;
  return 1;
}

/** Fill {product}, {staff}, {pet} in an event's text with the names it picked. */
function fill(state: GameState, co: Company, text: string, data: Record<string, any>): string {
  const prod = co.products.find((p) => p.id === data.product);
  const staff = co.staff.find((s) => s.id === data.staff);
  const pet = co.pets.find((p) => p.id === data.pet);
  const rival = data.rival ? C.rival[data.rival]?.name : undefined;
  const project = co.projects.find((p) => p.id === data.project) ?? co.projects[0];
  return text
    .replace(/\{(product|game)\}/g, prod?.design.name ?? "your latest game")
    .replace(/\{project\}/g, project?.design.name ?? "your next game")
    .replace(/\{theme\}/g, data.theme ? inSentence(C.theme[data.theme].name) : "one theme")
    .replace(/\{role\}/g, data.role ? C.role[data.role].name.toLowerCase() : "designer")
    .replace(/\{staff\}/g, staff?.name ?? "one of your team")
    .replace(/\{pet\}/g, pet?.name ?? "the office pet")
    .replace(/\{rival\}/g, rival ?? "a rival");
}

/** Event money in today's dollars, rounded the way a person would say it ($1,400, not $1,439). */
/**
 * How big a company is, for sizing what events do to it: cash scales with a year's income, fans with your fan base,
 * research with era costs. A small studio feels a $1,000 windfall; a giant feels a share of its year.
 */
export function eventScales(state: GameState, co: Company): { cashScale: number; fanScale: number; researchScale: number } {
  const idx = priceIndex(state.week);
  const yearly = co.ledger.reduce((s, w) => s + w.income, 0) * (WEEKS_PER_YEAR / Math.max(1, co.ledger.length));
  return {
    cashScale: Math.max(1, dpow(yearly / (BALANCE.eventScaleIncome * idx), 0.8)),
    fanScale: Math.max(1, dpow(co.fans / 20000, 0.8)),
    researchScale: eraCostMult(yearOf(state.week)),
  };
}

export function eventCash(amount: number, week: number, scale = 1): number {
  // windfalls grow with the company; losses grow more gently, so a bad week stings without sinking a growing studio
  const v = amount * priceIndex(week) * (amount < 0 ? Math.sqrt(scale) : scale);
  const a = Math.abs(v);
  const step = a >= 10_000 ? 500 : a >= 1_000 ? 100 : a >= 100 ? 10 : 5;
  return Math.sign(v) * Math.max(step, Math.round(a / step) * step);
}

/** A name inside a sentence: "dragons", "pets & vets", but "AI game-master box" keeps its capitals. */
export function inSentence(name: string): string {
  return name.split(" ").map((w) => (/^[A-Z]{2,}/.test(w) ? w : w.toLowerCase())).join(" ");
}

/** A short line under each option so the player can see what it will do. */
export function effectSummary(effects: Effect[] | undefined, week: number, data: Record<string, any> = {}): string {
  if (!effects?.length) return "Nothing changes";
  const parts: string[] = [];
  const signed = (n: number, what: string) => `${n >= 0 ? "+" : "−"}${digits(Math.abs(n))} ${what}`;
  const cash = (n: number) => `${n >= 0 ? "+" : "−"}${money(Math.abs(eventCash(n, week, data.cashScale ?? 1)))}`;
  const pctOf = (m: number) => `${m >= 1 ? "+" : "−"}${Math.round(Math.abs(m - 1) * 100)}%`;
  const weeks = (w: number) => (w === 0.5 ? "half a week" : w >= 52 ? `${Math.round(w / 52)} year${w >= 104 ? "s" : ""}` : `${w} week${w === 1 ? "" : "s"}`);
  const salesWord = (m: number) => (m === 2 ? "Double sales" : m === 3 ? "Triple sales" : `${pctOf(m)} sales`);
  for (const e of effects) {
    switch (e.type) {
      case "cash": parts.push(cash(e.amount)); break;
      case "rep": parts.push(signed(e.amount, "reputation")); break;
      case "fans": parts.push(signed(roundNice(e.amount * (data.fanScale ?? 1)), "fans")); break;
      case "morale": parts.push(signed(e.amount, "morale")); break;
      case "research": parts.push(`+${Math.round(e.amount * (data.researchScale ?? 1))} research`); break;
      case "xp": parts.push(`+${e.amount} experience for the team`); break;
      case "sales": parts.push(`${salesWord(e.mult)} for ${weeks(e.weeks)}`); break;
      case "audience": parts.push(`${pctOf(e.mult)} ${C.audience[e.audience]?.name ?? ""} sales`.replace("  ", " ")); break;
      case "costs": parts.push(`${pctOf(e.mult)} print costs for ${weeks(e.weeks)}`); break;
      case "margin": parts.push(`${pctOf(e.mult)} shop earnings for ${weeks(e.weeks)}`); break;
      case "printQuality": parts.push(`${e.amount >= 0 ? "better" : "worse"} print quality for ${weeks(e.weeks)}`); break;
      case "trend": {
        const id = e.theme === "random" ? data.theme : e.theme;
        parts.push(id && C.theme[id] ? `starts a ${C.theme[id].name} craze` : "starts a craze");
        break;
      }
      case "trendMech": parts.push(`starts a ${C.mechanic[e.mechanic]?.name ?? e.mechanic} craze`); break;
      case "hype": case "nextHype": parts.push(`+${Math.round(e.amount * 100)}% hype`); break;
      case "chance": {
        const yes = e.then?.length ? effectSummary(e.then, week, data) : "";
        const no = e.else?.length ? effectSummary(e.else, week, data) : "";
        const low = (t: string) => t.replace(/^[A-Z][a-z]/, (c) => c.toLowerCase());
        const pc = Math.round(e.p * 100);
        if (yes && no) parts.push(`${pc}% chance: ${low(yes)}; otherwise ${low(no)}`);
        else if (yes) parts.push(`${pc}% chance: ${low(yes)}; otherwise nothing`);
        else if (no) parts.push(`${100 - pc}% chance: ${low(no)}; otherwise nothing`);
        break;
      }
      case "royalty": parts.push(`${money(Math.abs(eventCash(e.weekly, week)))} a week for ${weeks(e.weeks)}`); break;
      case "investor": parts.push(`an investor takes ${Math.round(e.share * 100)}% of sales for ${weeks(e.weeks)}`); break;
      case "loan": parts.push(`a ${money(eventCash(e.amount, week))} loan`); break;
      case "creditBoost": parts.push("the bank will lend you more"); break;
      case "unlock": parts.push("unlocks something new"); break;
      case "workLoss": parts.push(`lose ${weeks(e.weeks)} of work`); break;
      case "staffLeaves": parts.push(e.after ? `they leave in ${weeks(e.after)}` : "they leave"); break;
      case "staffAway": parts.push(`they're away for ${weeks(e.weeks)}`); break;
      case "founderAway": parts.push(`you're away for ${weeks(e.weeks)}`); break;
      case "staffRest": parts.push(`tired staff rest for ${weeks(e.weeks)}`); break;
      case "staffStat": parts.push(`they get ${e.amount >= 0 ? "better" : "worse"} at their job`); break;
      case "starCandidate": case "legendary": parts.push("a star wants to join"); break;
      case "raise": parts.push(`+${Math.round(pct(e.pct) * 100)}% salaries`); break;
      case "stopCrunch": parts.push("no more late nights"); break;
      case "pet": parts.push("a new office pet"); break;
      case "room": parts.push("a free room"); break;
      case "revival": parts.push("an old game sells again"); break;
      case "stock": parts.push(signed(e.copies, "copies in stock")); break;
      case "stockLoss": parts.push(`lose ${Math.round(pct(e.pct) * 100)}% of your stock`); break;
      case "returns": parts.push(`${Math.round(pct(e.pct) * 100)}% of copies come back; you refund half the price`); break;
      case "recall": parts.push(data.recallCopies ? `pulls ${digits(Number(data.recallCopies))} copies (about ${money(data.recallCost ?? 0)} of stock)` : "every copy is pulled from the shops"); break;
      case "delayOrders": parts.push(`print orders arrive ${weeks(e.weeks)} late`); break;
      case "rivalRep": parts.push(`rivals ${e.amount >= 0 ? "gain" : "lose"} ${Math.abs(e.amount)} reputation`); break;
      case "rivalCopy": parts.push("a rival copies your game"); break;
      case "revealTrend": parts.push("learn the next craze early"); break;
      case "awardBoost": parts.push("better odds at the awards"); break;
      case "licence": parts.push("a licence offer"); break;
      case "nextSetFix": parts.push("your next expansion can fix it"); break;
      case "ban": parts.push("−2 reputation, +15% sales for 26 weeks"); break;
      case "buyCatalogue": parts.push("buy their old games; they pay you every week for 4 years"); break;
      case "arc": parts.push("starts a story"); break;
      default: break;
    }
  }
  return parts.length ? parts.join(", ").replace(/^./, (c) => c.toUpperCase()) : "Nothing changes";
}

/** The line under a story choice: what it does, or whether the story goes on. */
function arcDetail(o: { detail?: string; effects?: Effect[]; ending?: string; next?: string }, week: number, data: Record<string, any> = {}): string {
  if (o.detail) return o.detail;
  if (o.effects?.length) return effectSummary(o.effects, week, data);
  return o.ending || !o.next ? "Ends the story" : "The story goes on";
}

/** The option an autopilot (or a timed-out player) takes: the one with the least downside. */
function safestOption(options: { id: string; effects?: Effect[] }[], week: number): string {
  const score = (es: Effect[] | undefined): number => (es ?? []).reduce((s, e) => s + (e.type === "cash" ? e.amount * priceIndex(week) / 1000 : e.type === "rep" ? e.amount / 2 : e.type === "chance" ? e.p * score(e.then) + (1 - e.p) * score(e.else) : e.type === "staffLeaves" ? -3 : e.type === "sales" ? (e.mult - 1) * 3 : 0), 0);
  return [...options].sort((a, b) => score(b.effects) - score(a.effects))[0].id;
}

/** Random picks the text names (the craze theme, the star's job) are made when the event fires, so the words and the effect agree. */
function pickNamed(state: GameState, co: Company, e: EventDef, data: Record<string, any>): void {
  const all = JSON.stringify(e.options.map((o) => o.effects));
  const r = rng(state, `director:${co.id}`);
  if (!data.theme && all.includes('"theme":"random"')) data.theme = r.pick(themes.filter((t) => t.year <= yearOf(state.week))).id;
  if (!data.role && all.includes('"starCandidate"')) data.role = r.pick(STAR_ROLES.filter((x) => C.role[x] && C.role[x].year <= yearOf(state.week)));
  // a recall says what it costs: the stock and the copies at the printer
  if (all.includes('"recall"')) {
    const kids = all.includes('"target":"kids"');
    const p = launched(co).filter((x) => !kids || x.design.audience === "kids").sort((a, b) => b.releaseWeek - a.releaseWeek)[0];
    if (p) {
      const copies = p.stock + p.orders.reduce((a, o) => a + o.copies, 0);
      data.recallCopies = copies;
      data.recallCost = Math.round(copies * (p.unitCost || 0));
    }
  }
  if (!data.rival && (e.text.includes("{rival}") || e.title.includes("{rival}") || all.includes('"rivalCopy"'))) {
    const open = state.rivals.filter((rs) => C.rival[rs.id] && rivalActive(rs, C.rival[rs.id], state.week));
    if (open.length) data.rival = r.pick(open).id;
  }
}

const STAR_ROLES = ["designer", "artist", "writer", "developer", "marketer"];

function fireEvent(state: GameState, co: Company, e: EventDef, data: Record<string, any>): PendingDecision {
  pickNamed(state, co, e, data);
  Object.assign(data, eventScales(state, co));
  co.director.fired[e.id] = state.week;
  co.director.lastEventWeek = state.week;
  // nothing to decide: it's news, not a question, so it happens and shows as a note without stopping the clock
  if (e.options.length === 1) {
    const o = e.options[0];
    for (const x of o.effects ?? []) applyEffect(state, co, x, data, fill(state, co, e.title, data));
    log(state, "news_event", `${fill(state, co, e.title, data)}: ${fill(state, co, e.text, data)}`, co.id);
    return { id: `news-${e.id}-${state.week}`, kind: "event", week: state.week, title: fill(state, co, e.title, data), text: fill(state, co, e.text, data), options: [], defaultOption: o.id, expiresWeek: state.week, data: { ...data, event: e.id, news: true } };
  }
  return addDecision(state, co, {
    kind: "event",
    title: fill(state, co, e.title, data),
    text: fill(state, co, e.text, data),
    options: e.options.map((o) => ({ id: o.id, label: fill(state, co, o.label, data), detail: o.detail ?? effectSummary(o.effects, state.week, data) })),
    defaultOption: safestOption(e.options, state.week),
    weeks: 4,
    data: { ...data, event: e.id },
  });
}

/** Pick and fire one event for this company now (or nothing if none fits). Exposed for tests and the debug menu. */
export function triggerEvent(state: GameState, co: Company, forceId?: string): PendingDecision | null {
  const r = rng(state, `director:${co.id}`);
  const t = tension(state, co);
  co.director.tension = t;
  if (forceId) {
    const e = C.event[forceId];
    const data = e && resolveNeeds(state, co, e.when.needs ?? [], r);
    return e && data ? fireEvent(state, co, e, data) : null;
  }
  const options: { e: EventDef; data: Record<string, any> }[] = [];
  for (const e of events) {
    if (!eligible(state, co, e, t)) continue;
    const data = resolveNeeds(state, co, e.when.needs ?? [], r);
    if (data) options.push({ e, data });
  }
  if (!options.length) return null;
  const pick = r.weighted(options, (o) => o.e.weight * kindWeight(o.e.kind, t));
  return fireEvent(state, co, pick.e, pick.data);
}

// ------------------------------------------------------------------ effects

function productTarget(co: Company, target: string | undefined, data: Record<string, any>, r: Rng): Product | undefined {
  if (data.product) return co.products.find((p) => p.id === data.product);
  if (target === "random") return launched(co).length ? r.pick(launched(co)) : undefined;
  if (target === "oldBest") return launched(co).sort((a, b) => a.releaseWeek - b.releaseWeek).slice(0, Math.max(1, Math.ceil(launched(co).length / 2))).sort((a, b) => b.score - a.score)[0];
  return latest(co);
}

/** Apply one event effect. Cash is in 1965 dollars and scales with the price index. No effect can bankrupt the company. */
/** Diagnostics only: effect types to skip (balance ablation runs). Always empty in the game. */
export const SKIP_EFFECTS = new Set<string>();

export function applyEffect(state: GameState, co: Company, e: Effect, data: Record<string, any>, label: string): void {
  if (SKIP_EFFECTS.has(e.type)) return;
  const r = rng(state, `director:${co.id}`);
  const idx = priceIndex(state.week);
  const staff = co.staff.find((s) => s.id === data.staff);
  switch (e.type) {
    case "cash": {
      let amount = eventCash(e.amount, state.week, data.cashScale ?? eventScales(state, co).cashScale);
      // hard rule: an event can sting, never sink: losses are capped at half of what the company could raise
      if (amount < 0) amount = -Math.min(-amount, Math.max(0, (co.cash + creditLimit(state, co)) * 0.5));
      co.cash += Math.round(amount);
      break;
    }
    case "rep": co.reputation = clamp(co.reputation + e.amount, 0, 100); break;
    case "fans": co.fans = Math.max(0, co.fans + roundNice(e.amount * (data.fanScale ?? eventScales(state, co).fanScale))); break;
    case "morale": for (const s of co.staff) s.morale = clamp(s.morale + e.amount, 0, 100); break;
    case "research": co.research += Math.round(e.amount * (data.researchScale ?? eventScales(state, co).researchScale)); break;
    case "xp": for (const s of co.staff) s.xp += e.amount; break;
    case "sales": {
      const target = e.target === "all" ? "all" : e.target?.startsWith("theme:") ? e.target : `product:${productTarget(co, e.target, data, r)?.id ?? ""}`;
      addMod(state, co, { kind: "sales", value: e.mult, target, weeks: e.weeks, label });
      break;
    }
    case "audience": addMod(state, co, { kind: "audience", value: e.mult, target: e.audience, weeks: e.weeks, label }); break;
    case "costs": addMod(state, co, { kind: "costs", value: e.mult, target: "all", weeks: e.weeks, label }); break;
    case "margin": addMod(state, co, { kind: "margin", value: e.mult, target: "all", weeks: e.weeks, label }); break;
    case "printQuality": addMod(state, co, { kind: "printQuality", value: e.amount, target: "all", weeks: e.weeks, label }); break;
    case "royalty": addMod(state, co, { kind: "royalty", value: e.weekly, target: "all", weeks: e.weeks, label }); break;
    case "investor": addMod(state, co, { kind: "investor", value: e.share, target: "all", weeks: e.weeks, label }); break;
    case "trend": {
      const theme = e.theme === "random" ? data.theme ?? r.pick(themes.filter((t) => t.year <= yearOf(state.week))).id : e.theme;
      addWave(state, "theme", theme, e.strength, e.weeks, "director", `${C.theme[theme]?.name ?? theme} craze`);
      break;
    }
    case "trendMech": addWave(state, "mechanic", e.mechanic, e.strength, e.weeks, "director", `${C.mechanic[e.mechanic]?.name ?? e.mechanic} craze`); break;
    case "unlock":
      if (unlock(co, e.id)) log(state, "unlock", `New: ${unlockName(e.id)}.`, co.id);
      break;
    case "chance":
      for (const x of (r.chance(e.p) ? e.then : e.else) ?? []) applyEffect(state, co, x, data, label);
      break;
    case "hype": {
      const p = co.projects.find((x) => x.id === data.project) ?? co.projects[0];
      if (p) p.hype = clamp(p.hype + e.amount);
      else co.nextHype = clamp(co.nextHype + e.amount);
      break;
    }
    case "nextHype": co.nextHype = clamp(co.nextHype + e.amount); break;
    case "workLoss": {
      const p = co.projects.find((x) => x.id === data.project) ?? co.projects.find((x) => x.phase !== "ready");
      if (p) {
        const w = p.work[p.phase as keyof typeof p.work];
        if (w) w.done = Math.max(0, w.done - e.weeks * 10);
      }
      break;
    }
    case "revival": {
      const p = productTarget(co, e.target, data, r);
      if (p) {
        p.revivalWeek = state.week;
        p.revivalI = 0.3 + p.score / 25;
        log(state, "revival", `${p.design.name} is selling again. Reprint before the moment passes.`, co.id);
      }
      break;
    }
    case "starCandidate": {
      const role = data.role ?? r.pick(STAR_ROLES.filter((x) => C.role[x] && C.role[x].year <= yearOf(state.week)));
      const c = makeStaff(state, role, 5, r) as Candidate;
      c.ask = Math.round(c.salary * 1.2);
      c.salary = c.ask;
      co.candidates.unshift(c);
      log(state, "hire", `${c.name}, a star ${C.role[role].name.toLowerCase()}, is on your hiring list.`, co.id);
      break;
    }
    case "legendary": {
      const taken = new Set(state.companies.flatMap((x) => x.staff.map((s) => s.legendary)).filter(Boolean));
      const avail = legends.filter((l) => l.year <= yearOf(state.week) && !taken.has(l.id) && C.role[l.role]);
      if (!avail.length) break;
      const l = r.pick(avail);
      const c = makeStaff(state, l.role, 5, r) as Candidate;
      c.name = l.name;
      Object.assign(c.stats, l.stats);
      c.legendary = l.id;
      c.salary = c.ask = Math.round(l.salary * idx);
      co.candidates.unshift(c);
      log(state, "hire", `${l.name} would consider joining. Demand: ${l.demand.toLowerCase()}.`, co.id);
      break;
    }
    case "staffAway": if (staff) staff.awayUntil = state.week + e.weeks; break;
    case "founderAway": {
      const f = co.staff.find((s) => s.founder);
      if (f) f.awayUntil = state.week + e.weeks;
      break;
    }
    case "staffRest":
      for (const s of co.staff) if (s.burnout > 30 || s.id === data.staff) {
        s.burnout = 0;
        s.awayUntil = state.week + e.weeks;
      }
      break;
    case "staffLeaves":
      if (staff) staff.leavesWeek = state.week + (e.after ?? 0);
      break;
    case "staffStat":
      if (staff) {
        const k = (e.stat === "main" ? C.role[staff.role].main : e.stat) as keyof typeof staff.stats;
        staff.stats[k] = clamp(staff.stats[k] + e.amount, 1, 99);
      }
      break;
    case "raise": for (const s of co.staff) if (!s.founder) s.salary = Math.round(s.salary * (1 + pct(e.pct))); break;
    case "stopCrunch": for (const s of co.staff) s.crunch = false; break;
    case "rivalRep": for (const rv of state.rivals) if (!rv.bust) rv.reputation = clamp(rv.reputation + e.amount, 0, 100); break;
    case "pet": {
      const b = C.breed[e.breed];
      if (b && co.pets.length < 1 + ["garage", "office", "studio", "hq"].indexOf(co.level) * 2 + 1) {
        co.pets.push({ id: newId(state, "pet"), name: r.pick(["Biscuit", "Pepper", "Mochi", "Dice", "Meeple", "Captain"]), breed: b.id, spot: "in an empty game box", quirk: "sleeps in game boxes" });
      }
      break;
    }
    case "delayOrders": for (const p of co.products) for (const o of p.orders) o.arriveWeek += e.weeks; break;
    case "stock": {
      const p = productTarget(co, e.target, data, r);
      if (p) p.stock = Math.max(0, p.stock + e.copies);
      break;
    }
    case "stockLoss": for (const p of co.products) p.stock = Math.round(p.stock * (1 - pct(e.pct))); break;
    case "returns": {
      const p = productTarget(co, e.target, data, r);
      if (p) {
        const back = Math.round(Math.min(p.sold, p.sold * pct(e.pct)));
        p.sold -= back;
        p.stock += back;
        co.cash -= Math.round(back * p.price * 0.5);
      }
      break;
    }
    case "recall": {
      const p = launched(co).filter((x) => e.target !== "kids" || x.design.audience === "kids").sort((a, b) => b.releaseWeek - a.releaseWeek)[0];
      if (p) {
        p.stock = 0;
        for (const o of p.orders) o.copies = 0;
      }
      break;
    }
    case "loan": {
      const loan = makeLoan(state, "bank", eventCash(e.amount, state.week), 3 * WEEKS_PER_YEAR, e.rate);
      co.loans.push(loan);
      co.cash += loan.principal;
      break;
    }
    case "creditBoost": co.creditBonus += e.amount * idx; break;
    case "awardBoost": co.awardBoost += e.amount; break;
    case "achievement": grantFlag(co, e.id); break;
    case "licence": offerLicence(state, co, e.id); break;
    case "nextSetFix": grantFlag(co, "nextSetFix"); break;
    case "ban":
      co.reputation = clamp(co.reputation - 2, 0, 100);
      addMod(state, co, { kind: "sales", value: 1.15, target: `product:${productTarget(co, undefined, data, r)?.id ?? ""}`, weeks: 26, label });
      break;
    case "rivalCopy": {
      const p = latest(co);
      const rv = state.rivals.find((x) => x.id === data.rival) ?? state.rivals.find((x) => C.rival[x.id] && rivalActive(x, C.rival[x.id], state.week));
      if (p && rv) rv.copyQueue.push({ mechanic: p.design.mechanic, theme: p.design.theme, audience: p.design.audience, week: state.week + 8, of: p.marketId });
      break;
    }
    case "revealTrend": {
      const next = state.market.trends.filter((w) => w.start > state.week).sort((a, b) => a.start - b.start)[0];
      if (next) log(state, "trend", `A little bird says ${next.label} is coming around ${yearOf(next.start)}.`, co.id);
      break;
    }
    case "room": grantFlag(co, `freeRoom:${e.room}`); break;
    case "buyCatalogue": {
      const rv = state.rivals.find((x) => x.id === data.rival);
      if (rv?.catalogueFor) {
        co.cash -= Math.round(rv.catalogueFor);
        addMod(state, co, { kind: "royalty", value: rv.catalogueFor / 150 / idx, target: "all", weeks: 4 * WEEKS_PER_YEAR, label: `${C.rival[rv.id].name} back catalogue` });
        co.fans += 3000;
        rv.catalogueFor = undefined;
        grantFlag(co, "bought_catalogue");
      }
      break;
    }
    case "arc": startArc(state, co, e.id); break;
    default:
      break;
  }
}

/** Event data writes percentages either as 10 or 0.1; both mean ten percent. */
function pct(v: number): number {
  return v > 1 ? v / 100 : v;
}

function unlockName(id: string): string {
  const [kind, key] = [id.slice(0, id.indexOf(":")), id.slice(id.indexOf(":") + 1)];
  const map: Record<string, Record<string, { name: string }>> = { m: C.mechanic, t: C.theme, ch: C.channel, c: C.component, f: C.format };
  return map[kind]?.[key]?.name ?? key;
}

function grantFlag(co: Company, flag: string): void {
  if (!co.flags.includes(flag)) co.flags.push(flag);
}

// ------------------------------------------------------------------ story arcs

export function startArc(state: GameState, co: Company, id: string): ArcRun | null {
  const a = C.arc[id];
  if (!a || co.arcs.some((x) => x.id === id)) return null;
  const run: ArcRun = { id, step: a.steps[0].id, due: state.week, started: state.week, stepStarted: state.week, choices: [] };
  co.arcs.push(run);
  return run;
}

function arcStepCheck(state: GameState, co: Company, run: ArcRun): boolean {
  const step = C.arc[run.id].steps.find((s) => s.id === run.step);
  const c = step?.check;
  if (!c) return true;
  const since = state.week - (c.withinWeeks ?? 9999);
  return co.products.some((p) => p.launched && p.releaseWeek >= Math.max(since, run.started) && (!c.releasedTheme || p.design.theme === c.releasedTheme));
}

/** A week that never comes. Finite so it survives a save (JSON has no Infinity). */
export const NEVER = 1e9;

function offerArcStep(state: GameState, co: Company, run: ArcRun): void {
  const a = C.arc[run.id];
  const step = a.steps.find((s) => s.id === run.step)!;
  const ok = arcStepCheck(state, co, run);
  const opts = step.options.filter((o) => o.requires !== "check" || ok);
  run.due = NEVER; // waiting for the player's answer (not Infinity: saves are JSON, which turns it into null)
  run.stepStarted = state.week;
  const scales = eventScales(state, co);
  addDecision(state, co, {
    kind: "arc",
    title: `${a.name}: ${step.title}`,
    text: step.text,
    options: opts.map((o) => ({ id: o.id, label: o.label, detail: arcDetail(o, state.week, scales) })),
    defaultOption: safestOption(opts, state.week),
    weeks: 6,
    data: { arc: run.id, step: step.id, ...scales },
  });
}

function arcEligible(state: GameState, co: Company, id: string): boolean {
  const a = C.arc[id];
  const y = yearOf(state.week);
  if (co.arcs.some((x) => x.id === id)) return false;
  if (a.when.minYear && y < a.when.minYear) return false;
  if (a.when.maxYear && y > a.when.maxYear) return false;
  return resolveNeeds(state, co, a.when.needs ?? [], rng(state, `director:${co.id}`)) !== null;
}

// ------------------------------------------------------------------ decision handlers

DECISION_HANDLERS.event = (state, co, d, option) => {
  const e = C.event[d.data?.event];
  const o = e?.options.find((x) => x.id === option);
  if (!e || !o) return;
  co.stats.eventsAnswered += 1;
  if (e.id === "cat_box_viral") grantFlag(co, "cat_viral");
  for (const x of o.effects) applyEffect(state, co, x, d.data ?? {}, e.title);
  log(state, "event", `${d.title}: ${o.label}.`, co.id);
};

DECISION_HANDLERS.arc = (state, co, d, option) => {
  const run = co.arcs.find((x) => x.id === d.data?.arc && !x.ending);
  const step = C.arc[d.data?.arc]?.steps.find((s) => s.id === d.data?.step);
  const o = step?.options.find((x) => x.id === option);
  if (!run || !step || !o) return;
  co.stats.eventsAnswered += 1;
  run.choices.push(`${step.id}:${o.id}`);
  for (const x of o.effects ?? []) applyEffect(state, co, x, d.data ?? {}, C.arc[run.id].name);
  if (o.ending || !o.next) {
    run.ending = o.ending ?? "The story ends here.";
    run.doneWeek = state.week;
    co.stats.arcsDone += 1;
    log(state, "arc", `${C.arc[run.id].name}: ${run.ending}`, co.id);
  } else {
    run.step = o.next;
    run.due = state.week + (o.delayWeeks ?? 8);
  }
};

// ------------------------------------------------------------------ weekly

/** Expire timed modifiers, apply staff departures, then let the director pace the next surprise. */
export function directorWeek(state: GameState, co: Company): void {
  co.mods = co.mods.filter((m) => m.until > state.week);
  for (const s of [...co.staff]) {
    if (s.awayUntil !== undefined && state.week >= s.awayUntil) s.awayUntil = undefined;
    if (s.leavesWeek !== undefined && state.week >= s.leavesWeek) {
      co.staff = co.staff.filter((x) => x.id !== s.id);
      log(state, "quit", `${s.name} finished their passion project and left, as agreed.`, co.id);
    }
  }
  if (co.bankrupt) return;
  // story arcs: steps come due
  for (const run of co.arcs) if (!run.ending && state.week >= run.due) offerArcStep(state, co, run);
  // one surprise at a time
  if (co.pending.some((p) => p.kind === "event" || p.kind === "arc")) return;
  const dir = co.director;
  if (state.week >= dir.nextArcWeek) {
    const r = rng(state, `director:${co.id}`);
    const open = co.arcs.filter((x) => !x.ending).length;
    const avail = arcs.filter((a) => arcEligible(state, co, a.id));
    if (!open && avail.length) {
      const run = startArc(state, co, r.pick(avail).id);
      if (run) offerArcStep(state, co, run);
    }
    dir.nextArcWeek = state.week + r.int(3, 6) * WEEKS_PER_YEAR;
    return;
  }
  if (state.week < dir.nextWeek) return;
  // the first game is the tutorial: no surprises until it's out
  if (co.stats.released === 0) {
    dir.nextWeek = state.week + 4;
    return;
  }
  const fired = triggerEvent(state, co);
  const r = rng(state, `director:${co.id}`);
  const t = dir.tension;
  // bored players hear from the director sooner; struggling ones get a little breathing room first
  const gap = t === "bored" ? r.int(7, 11) : t === "struggling" ? r.int(9, 14) : r.int(11, 17);
  dir.nextWeek = state.week + (fired ? gap : 4);
}

export const directorSystem = {
  name: "director",
  week(state: GameState): void {
    for (const co of state.companies) {
      directorWeek(state, co);
      linesWeek(state, co);
      // every week, so one you've earned shows within a week (checking right after a command would make
      // the result depend on which player's commands ran first online)
      checkAchievements(state, co);
    }
  },
};

/**
 * QA only: every event and every story step as the player would see it, built without changing the game.
 * Placeholders are filled from this company (its latest game, first hire, first pet, first bust rival).
 */
export function previewDecisions(state: GameState, co: Company): PendingDecision[] {
  const prod = latest(co) ?? co.products[0];
  const base: Record<string, any> = { product: prod?.id, staff: co.staff.find((s) => !s.founder)?.id ?? co.staff[0]?.id, pet: co.pets[0]?.id, rival: state.rivals.find((r) => r.bust)?.id ?? state.rivals[0]?.id };
  const out: PendingDecision[] = [];
  for (const e of events) {
    const data = { ...base, project: co.projects[0]?.id };
    pickNamed(state, co, e, data);
    out.push({
      id: `qa-${e.id}`, kind: "event", week: state.week, title: fill(state, co, e.title, data), text: fill(state, co, e.text, data),
      options: e.options.map((o) => ({ id: o.id, label: fill(state, co, o.label, data), detail: o.detail ?? effectSummary(o.effects, state.week, data) })),
      defaultOption: safestOption(e.options, state.week), expiresWeek: state.week + 4, data: { ...data, event: e.id },
    });
  }
  for (const a of arcs) for (const step of a.steps) {
    out.push({
      id: `qa-${a.id}-${step.id}`, kind: "arc", week: state.week, title: `${a.name}: ${step.title}`, text: step.text,
      options: step.options.map((o) => ({ id: o.id, label: o.label, detail: arcDetail(o, state.week) })),
      defaultOption: safestOption(step.options, state.week), expiresWeek: state.week + 6, data: { arc: a.id, step: step.id },
    });
  }
  return out;
}

/** 1,234 → 1,200; 87,654 → 88,000: numbers that read like news, not maths. */
export function roundNice(n: number): number {
  const a = Math.abs(n);
  const step = a >= 100000 ? 1000 : a >= 10000 ? 500 : a >= 1000 ? 100 : 10;
  return Math.sign(n) * Math.round(a / step) * step;
}
